import * as React from 'react';
//@ts-ignore
import { BrowserProtocol, queryMiddleware } from 'farce';
import {
  createFarceRouter,
  createRender,
  makeRouteConfig,
  Route,
  RouteRenderArgs,
} from 'found';
//@ts-ignore
import { Resolver } from 'found-relay';
import environment, { readCookie } from './api/environment';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import 'react-dates/initialize';

import Wrapper from './views/preauth/Wrapper';
import Signup from './views/preauth/CustomerSignup';
import Logout from './views/app/Logout';
import MerchantSignup from './views/preauth/MerchantSignup';
import AccountSelect from './views/preauth/AccountSelect';
import Login from './views/preauth/Login';
import AppWrapper from './views/app/Wrapper';
import Subscriptions from './views/app/Subscriptions';
import ApiList from './views/app/ApiList';
import Api from './views/app/Api';
import Subscription from './views/app/Subscription';
import AccountSettings from './views/app/AccountSettings';
import { graphql, loadQuery, RelayEnvironmentProvider } from 'react-relay';
import { getCurrentAccount } from './account_helpers';
import { routes } from './routes';
import { PopupProvider } from './components/Popup/PopupProvider';
import NewAccountPopup from './components/Popups/NewAccountPopup';
import CreateAPI from './views/app/flows/CreateAPI';
import MenuWrapper from './views/app/MenuWrapper';
import UpdateAPI from './views/app/flows/UpdateAPI';
import { ToastProvider } from './components/Toast/ToastProvider';
import CreateAccount from './views/app/flows/CreateAccount';
import CreateCharge from './views/app/flows/CreateCharge';
import Subscribe from './views/app/Subscribe/Subscribe';
import UpdateSubscriberCard from './components/Popups/UpdateSubscriberCard';
import UpdateCharge from './views/app/flows/UpdateCharge';
import { addAccountID } from './helpers';
import ManagerMenuWrapper from './views/app/ManagerMenuWrapper';
import OnboardMerchant from './views/app/flows/OnboardMerchant';
import MerchantBilling from './views/app/MerchantBilling';
import moment from 'moment';
import Activity from './views/app/Activity';
import UserAccount from './views/app/UserAccount';
import configs from './configs';
import Loader from './components/Loader';
import DefaultErrorBoundary from './components/DefaultErrorBoundary';
import ForgotPassword from './views/preauth/ForgotPassword';
import ResetPassword from './views/preauth/ResetPassword';
import type { MerchantBilling_Query as MerchantBilling_QueryType } from './views/app/__generated__/MerchantBilling_Query.graphql';
import MerchantBilling_Query from './views/app/__generated__/MerchantBilling_Query.graphql';
import AddCurrency from './views/app/flows/AddCurrency';

const isLoggedIn = () => {
  const loggedin = readCookie('loggedin');

  return loggedin && new Date(loggedin).getTime() > new Date().getTime();
};

const protectedRenderer = (
  Comp: React.ReactNode,
  protect = true,
  redirect = '/preauth/login'
) => (args: any) => {
  // Check if the loggedin cookie has a timestamp for after now.
  if (protect && !isLoggedIn()) {
    console.log('Redirecting', isLoggedIn());
    args.match.router.push(redirect);
    return null;
  }

  if (!args.props) {
    return <Loader loading={!!args.props} full error={args.error} />;
  }

  //@ts-ignore
  return <Comp {...args.props} />;
};

const loadingWrapper = (Comp: React.ReactNode) => (args: any) => {
  if (!args.props) {
    return <Loader loading={!!args.props} error={args.error} />;
  }

  //@ts-ignore
  return <Comp {...args.props} />;
};

const errorBoundaryWrapper = (Comp: React.ReactNode) => (
  args: RouteRenderArgs
) => (children: any) => {
  return (
    <DefaultErrorBoundary>
      {/*@ts-ignore*/}
      <Comp {...args.props}>{children}</Comp>
    </DefaultErrorBoundary>
  );
};

const Router = createFarceRouter({
  historyProtocol: new BrowserProtocol(),
  historyMiddlewares: [queryMiddleware],
  routeConfig: makeRouteConfig(
    <Route>
      <Route
        path='/preauth'
        Component={Wrapper}
        render={(args: RouteRenderArgs) => {
          // Check if the loggedin cookie has a timestamp for after now.
          if (isLoggedIn() && getCurrentAccount()) {
            args.match.router.push('/subscriptions');
            console.log('Logging in', isLoggedIn());
            return <div>Loading</div>;
          }

          if (!args.props) {
            return null;
          }
          //@ts-ignore
          return <Wrapper {...args.props} />;
        }}
      >
        <Route path='/login' Component={Login} />
        <Route path='/forgot' Component={ForgotPassword} />
        <Route path='/reset' Component={ResetPassword} />
        <Route path='/account-select' Component={AccountSelect} />
        <Route path='/customer-sign-up' Component={Signup} />
        <Route path='/merchant-sign-up' Component={MerchantSignup} />
      </Route>
      <Route
        path='/subscribe/:serviceID'
        Component={Subscribe}
        query={graphql`
          query App_Subscribe_Query($serviceID: ID!) {
            viewer {
              ...Subscribe_viewer
            }
            service: node(id: $serviceID) {
              ...Subscribe_service
            }
            subscriptionFee
          }
        `}
        render={(args: any) => {
          console.log('args', args);
          if (!args.props) {
            return <div></div>;
          }
          return <Subscribe {...args.props} />;
        }}
      />
      <Route
        path='/'
        Component={AppWrapper}
        render={protectedRenderer(AppWrapper)}
        query={graphql`
          query App_Wrapper_Query($accountID: ID) {
            viewer {
              ...Wrapper_viewer
            }
            account(input: { accountID: $accountID }) {
              ...Wrapper_account
            }
          }
        `}
        prepareVariables={(params: any) => {
          const accountID = getCurrentAccount();
          if (!accountID) {
            window.location.href = routes.login;
          }
          return {
            ...params,
            accountID,
          };
        }}
      >
        <Route
          Component={MenuWrapper}
          render={errorBoundaryWrapper(MenuWrapper)}
        >
          <Route
            path='/'
            render={(args) => {
              args.match.router.push(routes.login);
              return null;
            }}
          />
          <Route path='/logout' Component={Logout} />
          <Route path={routes.onboardMerchant} Component={OnboardMerchant} />
          <Route
            path='/subscriptions'
            Component={Subscriptions}
            query={graphql`
              query App_Subscriptions_Query(
                $accountID: ID
                $before: Time!
                $after: Time!
              ) {
                account(input: { accountID: $accountID }) {
                  ...Subscriptions_account
                    @arguments(before: $before, after: $after)
                }
              }
            `}
            prepareVariables={(params: any) => {
              const newParams = { ...params };
              newParams.before = moment().endOf('day').toISOString();
              newParams.after = moment().startOf('day').toISOString();
              return addAccountID(newParams);
            }}
            render={loadingWrapper(Subscriptions)}
          />
          <Route
            path={routes.activity}
            Component={Activity}
            query={graphql`
              query App_Activity_Query($accountID: ID) {
                account(input: { accountID: $accountID }) {
                  ...Activity_account
                }
              }
            `}
            prepareVariables={(params: any) => {
              const newParams = { ...params };
              return addAccountID(newParams);
            }}
            render={loadingWrapper(Activity)}
          />
          <Route
            path='/subscriptions/:subscriptionID'
            Component={Subscription}
            query={graphql`
              query App_Subscription_Query($subscriptionID: ID!) {
                subscription: node(id: $subscriptionID) {
                  ... on Subscription {
                    ...Subscription_subscription
                  }
                }
                subscriptionFee
              }
            `}
            render={loadingWrapper(Subscription)}
          />
          <Route
            path={`${routes.accountSettings}/:tab?`}
            Component={AccountSettings}
            query={graphql`
              query App_AccountSettings_Query($accountID: ID) {
                account(input: { accountID: $accountID }) {
                  ...AccountSettings_account
                }
              }
            `}
            prepareVariables={addAccountID}
            render={loadingWrapper(AccountSettings)}
          />
          <Route path={routes.createAccount} Component={CreateAccount} />
          <Route
            path={routes.userProfile}
            Component={UserAccount}
            query={graphql`
              query App_UserAccount_Query {
                viewer {
                  ...UserAccount_viewer
                }
              }
            `}
            render={loadingWrapper(UserAccount)}
          />
        </Route>
        <Route
          Component={ManagerMenuWrapper}
          render={errorBoundaryWrapper(ManagerMenuWrapper)}
        >
          <Route
            path={routes.apis}
            Component={ApiList}
            query={graphql`
              query App_ApiList_Query(
                $accountID: ID
                $before: Time!
                $after: Time!
              ) {
                account(input: { accountID: $accountID }) {
                  ...ApiList_account @arguments(before: $before, after: $after)
                }
              }
            `}
            prepareVariables={(params: any) => {
              const newParams = { ...params };
              const today = new Date();
              newParams.before = today.toISOString();
              newParams.after = new Date(
                today.getTime() - 5 * 24 * 60 * 60 * 1000
              ).toISOString();

              return addAccountID(newParams);
            }}
            render={loadingWrapper(ApiList)}
          />
          <Route
            path={`${routes.api}/:serviceID`}
            Component={Api}
            query={graphql`
              query App_Api_Query(
                $serviceID: ID!
                $accountID: ID!
                $before7: Time!
                $after7: Time!
                $before30: Time!
                $after30: Time!
                $beforeYear: Time!
                $afterYear: Time!
              ) {
                service: node(id: $serviceID) {
                  ... on Service {
                    ...Api_service
                      @arguments(
                        before7: $before7
                        after7: $after7
                        before30: $before30
                        after30: $after30
                        beforeYear: $beforeYear
                        afterYear: $afterYear
                      )
                  }
                }
                account(input: { accountID: $accountID }) {
                  ...Api_account
                }
              }
            `}
            prepareVariables={(params: any) => {
              const newParams = { ...params };

              // 7 day
              newParams.before7 = moment().toISOString();
              newParams.after7 = moment().subtract(7, 'days').toISOString();

              // 30 days
              newParams.before30 = moment().toISOString();
              newParams.after30 = moment().subtract(30, 'days').toISOString();

              // year
              newParams.beforeYear = moment().toISOString();
              newParams.afterYear = moment().startOf('year').toISOString();

              return addAccountID(newParams);
            }}
            render={loadingWrapper(Api)}
          />
          <Route
            path={routes.merchantBilling}
            Component={loadingWrapper(MerchantBilling)}
            render={(args: any) => {
              const initialQueryRef = loadQuery<MerchantBilling_QueryType>(
                environment,
                MerchantBilling_Query,
                {
                  accountID: getCurrentAccount(),
                  financesBefore: moment().endOf('day').toISOString(),
                  financesAfter: moment().subtract(30, 'days').toISOString(),
                }
              );

              if (!initialQueryRef) {
                return null;
              }
              return (
                <React.Suspense fallback={<Loader loading={true} />}>
                  <MerchantBilling queryRef={initialQueryRef} />
                </React.Suspense>
              );
            }}
          />
          <Route path={routes.createAPI} Component={CreateAPI} />
          <Route
            path={`${routes.createCharge}/:serviceID`}
            Component={CreateCharge}
            query={graphql`
              query App_CreateCharge_Query($serviceID: ID!) {
                service: node(id: $serviceID) {
                  ... on Service {
                    ...CreateCharge_service
                  }
                }
              }
            `}
          />
          <Route
            path={`${routes.addCurrency}/:serviceID`}
            Component={AddCurrency}
            query={graphql`
              query App_AddCurrency_Query($serviceID: ID!) {
                service: node(id: $serviceID) {
                  ... on Service {
                    ...AddCurrency_service
                  }
                }
              }
            `}
          />
          <Route
            path={`${routes.updateApi}/:serviceID`}
            Component={UpdateAPI}
            query={graphql`
              query App_UpdateAPI_Query($serviceID: ID!) {
                service: node(id: $serviceID) {
                  ... on Service {
                    ...UpdateAPI_service
                  }
                }
              }
            `}
            render={loadingWrapper(UpdateAPI)}
          />
          <Route
            path={`${routes.updateCharge}/:chargeID`}
            Component={UpdateCharge}
            query={graphql`
              query App_UpdateCharge_Query($chargeID: ID!) {
                charge: node(id: $chargeID) {
                  ... on Charge {
                    ...UpdateCharge_charge
                  }
                }
              }
            `}
            render={loadingWrapper(UpdateCharge)}
          />
        </Route>
      </Route>
    </Route>
  ),
  render: createRender({}),
});

const stripePromise = loadStripe(configs?.stripe_pk ?? '');

const App = () => (
  <RelayEnvironmentProvider environment={environment}>
    <ToastProvider>
      <Elements stripe={stripePromise}>
        <PopupProvider
          popups={{
            'new-account': {
              component: NewAccountPopup,
              name: 'New Account',
            },
            'update-subscriber-card': {
              component: UpdateSubscriberCard,
              name: 'Update Subscriber Card',
            },
          }}
        >
          <Router resolver={new Resolver(environment)} />
        </PopupProvider>
      </Elements>
    </ToastProvider>
  </RelayEnvironmentProvider>
);

export default App;
